import { useEffect } from 'react';

/**
 * Sets the document title while the component is mounted, putting back whatever was there on the way
 * out.
 *
 * `index.html` carries one title for the whole bundle — "HR Hub" — which is the right name for the
 * signed-in app and the wrong one for a public form. Those are reached by a link out of an email and
 * nothing else, and the title is the first thing a screen reader says on arrival: someone who has
 * been sent two of these links and has both open hears the same four letters twice and has to read
 * into the page to tell which is which. WCAG 2.4.2 asks for a title that describes the page, and
 * since this app paints every route from one HTML file, that has to be set from the route.
 *
 * Restoring the previous title matters less than it looks — the public routes never unmount into
 * anything else, since `main.tsx` picks `PublicApp` or the hub once per load — but a hook that leaves
 * a title behind would be a trap the first time one is used inside the hub.
 */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;

    return () => {
      document.title = previous;
    };
  }, [title]);
}
